import { useState, useRef, useEffect } from "react";
import { Globe } from "lucide-react";
import { useLanguage, Language, stripLangPrefix, addLangPrefix } from "@/hooks/useLanguage";
import { cn } from "@/lib/utils";

const languages: { code: Language; label: string; flag: string }[] = [
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
];

interface LanguageSwitcherProps {
  variant?: 'icon' | 'full';
  className?: string;
}

const LanguageSwitcher = ({ variant = 'icon', className }: LanguageSwitcherProps) => {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentLanguage = languages.find(l => l.code === language);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  const handleSelect = (code: Language) => {
    setLanguage(code);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={handleClick}
        className={cn(
          "flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors focus:outline-none",
          className
        )}
      >
        <Globe className="w-4 h-4" />
        {variant === 'full' && (
          <span>{currentLanguage?.flag} {currentLanguage?.code.toUpperCase()}</span>
        )}
      </button>
      
      {isOpen && (
        <div 
          className="absolute right-0 top-full mt-2 min-w-[120px] z-[100] bg-background border border-border rounded-md shadow-md py-1 animate-fade-in"
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={cn(
                "flex items-center gap-2 w-full px-3 py-2 text-sm cursor-pointer hover:bg-accent transition-colors text-left",
                language === lang.code && "bg-secondary"
              )}
            >
              <span>{lang.flag}</span>
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
